import React, { useState } from 'react';
import { Globe, CheckCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { seoService } from '../../../services/seoService';

const SitemapManager = ({ sitemap, onRefresh }) => {
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({
    sitemapUrl: sitemap?.sitemap_url || '',
    indexingStatus: sitemap?.indexing_status || 'pending',
    pagesIndexed: sitemap?.pages_indexed || 0,
    totalPages: sitemap?.total_pages || 0
  });

  const handleUpdate = async (e) => {
    e?.preventDefault();
    try {
      setUpdating(true);
      await seoService?.updateSitemapConfig({
        sitemapUrl: formData?.sitemapUrl,
        lastGenerated: new Date()?.toISOString(),
        lastSubmitted: formData?.indexingStatus === 'submitted' ? new Date()?.toISOString() : sitemap?.last_submitted,
        indexingStatus: formData?.indexingStatus,
        pagesIndexed: formData?.pagesIndexed,
        totalPages: formData?.totalPages
      });
      onRefresh?.();
      alert('Configuration du sitemap mise à jour avec succès');
    } catch (error) {
      console.error('Error updating sitemap:', error);
      alert('Erreur lors de la mise à jour du sitemap');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'indexed':
        return 'bg-green-100 text-green-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'indexed':
        return 'Indexé';
      case 'submitted':
        return 'Soumis';
      case 'error':
        return 'Erreur';
      case 'not_configured':
        return 'Non configuré';
      default:
        return 'En attente';
    }
  };

  const indexingPercentage = formData?.totalPages > 0
    ? ((formData?.pagesIndexed / formData?.totalPages) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <Globe className="w-6 h-6 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Gestion du Sitemap</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Statut d'indexation</p>
            <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(formData?.indexingStatus)}`}>
              {getStatusLabel(formData?.indexingStatus)}
            </span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Pages indexées</p>
            <p className="text-2xl font-bold text-gray-900">{formData?.pagesIndexed}/{formData?.totalPages}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Taux d'indexation</p>
            <p className="text-2xl font-bold text-gray-900">{indexingPercentage}%</p>
          </div>
        </div>

        {/* Progress Bar */}
        {formData?.totalPages > 0 && (
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${indexingPercentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Configuration Form */}
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL du Sitemap</label>
            <div className="flex space-x-2">
              <input
                type="url"
                value={formData?.sitemapUrl}
                onChange={(e) => setFormData({ ...formData, sitemapUrl: e?.target?.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder="https://adtexpress.cloud/sitemap.xml"
              />
              {formData?.sitemapUrl && (
                <a
                  href={formData?.sitemapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <ExternalLink size={18} />
                </a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut d'indexation</label>
              <select
                value={formData?.indexingStatus}
                onChange={(e) => setFormData({ ...formData, indexingStatus: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              >
                <option value="pending">En attente</option>
                <option value="submitted">Soumis</option>
                <option value="indexed">Indexé</option>
                <option value="error">Erreur</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pages indexées</label>
              <input
                type="number"
                min="0"
                value={formData?.pagesIndexed}
                onChange={(e) => setFormData({ ...formData, pagesIndexed: parseInt(e?.target?.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total de pages</label>
              <input
                type="number"
                min="0"
                value={formData?.totalPages}
                onChange={(e) => setFormData({ ...formData, totalPages: parseInt(e?.target?.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updating}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>Mise à jour...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  <span>Mettre à jour</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Google Search Console Integration */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <Globe className="w-6 h-6 text-blue-600 mr-3 mt-1" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">Google Search Console</h4>
            <p className="text-sm text-blue-800 mb-3">
              Pour soumettre votre sitemap à Google et suivre l'indexation en temps réel, utilisez Google Search Console.
            </p>
            <div className="space-y-2 text-sm text-blue-800 mb-4">
              <p>1. Connectez-vous à Google Search Console</p>
              <p>2. Sélectionnez votre propriété (adtexpress.cloud)</p>
              <p>3. Allez dans "Sitemaps" dans le menu latéral</p>
              <p>4. Ajoutez l'URL de votre sitemap et cliquez sur "Envoyer"</p>
            </div>
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Ouvrir Search Console
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapManager;